import type { Brand } from './brand'

/**
 * A local calendar date in `YYYY-MM-DD` form (D-034).
 *
 * This is the user's local date at the moment of entry. It is stored as a
 * string and never re-derived from a timestamp afterwards, so a later timezone
 * change cannot move an existing entry to a different day.
 */
export type CalendarDate = Brand<string, 'CalendarDate'>

/** An absolute instant, ISO 8601 with milliseconds and a `Z` suffix. */
export type IsoTimestamp = Brand<string, 'IsoTimestamp'>

/**
 * A month, with **no day** (D-068).
 *
 * Anything the app states about the future is stated to the month, never to the
 * day: a projected calendar date reads as a commitment the underlying trend
 * cannot support, and a recommended route's estimated duration carries more
 * implied authority than a weekly nudge, not less.
 *
 * The enforcement is structural rather than a formatting convention — there is
 * no day here to render by mistake. **Do not "improve" this into a
 * `CalendarDate`.** A `CalendarDate` returned from a forward-looking figure
 * would be one careless `formatCalendarDate` away from promising a Tuesday.
 *
 * It lives here, beside the other date types, rather than inside
 * `calculations/projection.ts`, because the route engine states a duration too
 * and `rules/` must not import the projection module.
 */
export interface ApproximateMonth {
  readonly year: number
  /** 1–12. */
  readonly month: number
}

/**
 * A local wall-clock time in `HH:MM` form, 24-hour (D-099).
 *
 * A routine anchor is a named window with a start time, and this is that start
 * time. **It is wall-clock and carries no date and no offset**, which is the
 * whole point: "07:30" means half past seven wherever the user is and whatever
 * the clocks have done overnight. Storing an instant instead would make an
 * anchor drift by an hour twice a year, and travel would move it further.
 *
 * The window it opens runs to the end of that local day.
 */
export type TimeOfDay = Brand<string, 'TimeOfDay'>

const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/

/**
 * Validate and brand a wall-clock time.
 *
 * Strict about the shape so that `"7:30"`, `"24:00"` and `"07:60"` are all
 * rejected rather than silently accepted and then compared as strings — these
 * values are ordered lexicographically, which only works while every one of them
 * is zero-padded and in range.
 */
export function toTimeOfDay(value: string): TimeOfDay | undefined {
  return TIME_OF_DAY_PATTERN.test(value) ? (value as TimeOfDay) : undefined
}

/** The user's *local* wall-clock time for a given instant. */
export function timeOfDayFrom(instant: Date): TimeOfDay {
  const hours = String(instant.getHours()).padStart(2, '0')
  const minutes = String(instant.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}` as TimeOfDay
}

/** Unwrap for display or comparison only. */
export function unwrapTime(value: TimeOfDay): string {
  return value as string
}

/**
 * Validate and brand a calendar date string. Rejects both malformed input and
 * impossible dates such as `2026-02-30`, which `new Date()` would silently roll
 * over into March.
 */
export function toCalendarDate(value: string): CalendarDate | undefined {
  if (!CALENDAR_DATE_PATTERN.test(value)) return undefined

  const [yearText, monthText, dayText] = value.split('-')
  if (yearText === undefined || monthText === undefined || dayText === undefined) return undefined

  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)

  if (month < 1 || month > 12) return undefined
  if (day < 1 || day > daysInMonth(year, month)) return undefined

  return value as CalendarDate
}

export function daysInMonth(year: number, month: number): number {
  // Day 0 of the following month is the last day of this one.
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

/**
 * The user's *local* calendar date for a given instant.
 *
 * Deliberately uses local getters rather than `toISOString()`, which would
 * return the UTC date and silently shift entries across midnight for anyone
 * west of Greenwich.
 */
export function calendarDateFrom(instant: Date): CalendarDate {
  const year = String(instant.getFullYear()).padStart(4, '0')
  const month = String(instant.getMonth() + 1).padStart(2, '0')
  const day = String(instant.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}` as CalendarDate
}

export function toIsoTimestamp(instant: Date): IsoTimestamp {
  return instant.toISOString() as IsoTimestamp
}

export function parseIsoTimestamp(value: string): IsoTimestamp | undefined {
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? undefined : (value as IsoTimestamp)
}

/** Unwrap for serialization or comparison only. */
export function unwrapDate(value: CalendarDate | IsoTimestamp): string {
  return value as string
}
